import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useOperator } from '@/lib/operator-context';
import {
  getOperatorConversation,
  apiFetch,
} from '@/lib/operator-api';
import type { OperatorConversation, OperatorMessage } from '@/lib/operator-api/types';
import { getFlowContextFromPriorityState } from '@/lib/flow-conversation-mapping';
import { useJourneyEvents } from '@/hooks/useFlowData';
import { ConversationPriorityState } from '@/lib/priority/types';
import CopilotSuggestionPanel from '@/components/operator/CopilotSuggestionPanel';
import { ChatHeader } from '@/components/conversations/ChatHeader';
import { MessageList } from '@/components/conversations/MessageList';
import { ComposeBar } from '@/components/conversations/ComposeBar';
import { ContactPanel } from '@/components/conversations/ContactPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, Lock, ArrowLeft } from 'lucide-react';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function ConversationDetailPage() {
  const { id: conversationId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { operator, permissions, isLoading: authLoading } = useOperator();
  const [conversation, setConversation] = useState<OperatorConversation | null>(null);
  const [messages, setMessages] = useState<OperatorMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compose state
  const [composeText, setComposeText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Template state
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templates, setTemplates] = useState<Array<{ slug: string; template: { bodyText: string } }>>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Product & flow state
  const [products, setProducts] = useState<Array<{ id: string; name: string; priceCents: number }>>([]);
  const [flows, setFlows] = useState<Array<{ id: string; name: string }>>([]);

  // Assignment state
  const [availableOperators, setAvailableOperators] = useState<Array<{ id: string; name: string; email: string; role: string }>>([]);
  const [isAssignmentLoading, setIsAssignmentLoading] = useState(false);
  const [showTransferDropdown, setShowTransferDropdown] = useState(false);

  // Info panel state
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);

  // Fetch journey events
  const {
    events: journeyEvents,
    isLoading: journeyLoading,
    error: journeyError,
    refetch: refetchJourney,
  } = useJourneyEvents({
    conversationId,
    enabled: !!conversation,
  });

  const canViewConversations = permissions?.canViewConversations ?? false;
  const operatorId = operator?.id;

  // Check if outside 24h messaging window
  // Falls back to most recent incoming message if lastCustomerMessageAt is not set
  const isOutsideWindow = useMemo(() => {
    let lastIncoming: string | null | undefined = conversation?.lastCustomerMessageAt;
    if (!lastIncoming) {
      const lastIncomingMsg = [...messages].reverse().find(m => m.direction === 'INCOMING');
      lastIncoming = lastIncomingMsg?.timestamp;
    }
    if (!lastIncoming) return true;
    const hoursSince = (Date.now() - new Date(lastIncoming).getTime()) / (1000 * 60 * 60);
    return hoursSince > 24;
  }, [conversation?.lastCustomerMessageAt, messages]);

  // Fetch conversation
  useEffect(() => {
    if (!operatorId || !canViewConversations || !conversationId) return;
    const controller = new AbortController();

    const fetchConversation = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getOperatorConversation(conversationId);
        if (controller.signal.aborted) return;
        if (result && result.conversation) {
          setConversation(result.conversation);
          setMessages(result.messages || []);
          setError(null);
        } else {
          setError(t('conversations.detail.notFound'));
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : t('conversations.compose.sendError'));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchConversation();
    return () => controller.abort();
  }, [operatorId, canViewConversations, conversationId, t]);

  // Fetch templates (always, so they're available via toolbar inside window too)
  useEffect(() => {
    if (!conversation?.businessId) return;
    const controller = new AbortController();
    const fetchTemplates = async () => {
      try {
        const res = await apiFetch(
          `/api/operator/templates?businessId=${encodeURIComponent(conversation.businessId)}`,
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        const data = await res.json();
        if (res.ok && data.success) {
          setTemplates(data.data.templates || []);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Failed to fetch templates:', err);
      }
    };
    fetchTemplates();
    return () => controller.abort();
  }, [conversation?.businessId]);

  // Fetch products for the business
  useEffect(() => {
    if (!conversation?.businessId) return;
    const controller = new AbortController();
    const fetchProducts = async () => {
      try {
        const res = await apiFetch(
          `/api/operator/catalog/products?businessId=${encodeURIComponent(conversation.businessId)}`,
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        const data = await res.json();
        if (res.ok && data.success) {
          setProducts(data.data?.products || []);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Failed to fetch products:', err);
      }
    };
    fetchProducts();
    return () => controller.abort();
  }, [conversation?.businessId]);

  // Fetch flows for the business
  useEffect(() => {
    if (!conversation?.businessId) return;
    const controller = new AbortController();
    const fetchFlows = async () => {
      try {
        const res = await apiFetch(
          `/api/operator/flows?businessId=${encodeURIComponent(conversation.businessId)}`,
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        const data = await res.json();
        if (res.ok && data.success) {
          setFlows(data.data?.flows || data.flows || []);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Failed to fetch flows:', err);
      }
    };
    fetchFlows();
    return () => controller.abort();
  }, [conversation?.businessId]);

  // Map currentStep to ConversationPriorityState
  const priorityState = useMemo((): ConversationPriorityState => {
    if (!conversation) return ConversationPriorityState.IDLE;
    const stepMap: Record<string, ConversationPriorityState> = {
      'IDLE': ConversationPriorityState.IDLE,
      'NEW_INQUIRY': ConversationPriorityState.NEW_INQUIRY,
      'BOOKING_IN_PROGRESS': ConversationPriorityState.BOOKING_IN_PROGRESS,
      'AWAITING_PAYMENT': ConversationPriorityState.AWAITING_PAYMENT,
      'PAYMENT_SENT': ConversationPriorityState.PAYMENT_SENT,
      'PAYMENT_OVERDUE': ConversationPriorityState.PAYMENT_OVERDUE,
      'PAID': ConversationPriorityState.PAID,
      'CONFIRMED': ConversationPriorityState.CONFIRMED,
      'APPOINTMENT_DAY': ConversationPriorityState.APPOINTMENT_DAY,
      'COMPLETED': ConversationPriorityState.COMPLETED,
      'NO_SHOW': ConversationPriorityState.NO_SHOW,
      'CANCELLED': ConversationPriorityState.CANCELLED,
    };
    return stepMap[conversation.currentStep] ?? ConversationPriorityState.IDLE;
  }, [conversation]);

  // Derive flow context from priority state
  const flowContext = useMemo(() => {
    if (!conversation) return null;
    return getFlowContextFromPriorityState(
      priorityState,
      new Date(conversation.lastMessageAt)
    );
  }, [priorityState, conversation]);

  // Send message handler
  const handleSend = useCallback(async () => {
    if (!composeText.trim() || isSending || !conversationId) return;
    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);

    try {
      const res = await apiFetch(
        `/api/operator/conversations/${encodeURIComponent(conversationId)}/actions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'send_message', content: composeText.trim() }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSendError(data.error || t('conversations.compose.sendError'));
        return;
      }
      if (data.message) {
        setMessages((prev: OperatorMessage[]) => [...prev, {
          ...data.message,
          direction: 'OUTGOING',
          text: composeText.trim(),
        }]);
      }
      setComposeText('');
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch {
      setSendError(t('conversations.compose.sendError'));
    } finally {
      setIsSending(false);
    }
  }, [composeText, isSending, conversationId, t]);

  // Send template handler
  const handleSendTemplate = useCallback(async () => {
    if (!selectedTemplate || isSending || !conversationId) return;
    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);

    try {
      const res = await apiFetch(
        `/api/operator/conversations/${encodeURIComponent(conversationId)}/actions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'send_template',
            templateName: selectedTemplate,
            languageCode: 'pt_BR'
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSendError(data.error || t('conversations.compose.sendError'));
        return;
      }
      if (data.message) {
        setMessages((prev: OperatorMessage[]) => [...prev, {
          ...data.message,
          direction: 'OUTGOING',
          text: t('conversations.compose.templateSent'),
        }]);
      }
      setSelectedTemplate('');
      setShowTemplateSelector(false);
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch {
      setSendError(t('conversations.compose.sendError'));
    } finally {
      setIsSending(false);
    }
  }, [selectedTemplate, isSending, conversationId, t]);

  // Send product handler
  const handleSendProduct = useCallback(async (productId: string) => {
    if (isSending || !conversationId) return;
    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);
    try {
      const res = await apiFetch(
        `/api/operator/conversations/${encodeURIComponent(conversationId)}/actions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'send_product', productId }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSendError(data.error || t('conversations.compose.sendError'));
        return;
      }
      if (data.message) {
        setMessages((prev: OperatorMessage[]) => [...prev, {
          ...data.message,
          direction: 'OUTGOING',
          text: t('conversations.compose.productSent'),
        }]);
      }
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch {
      setSendError(t('conversations.compose.sendError'));
    } finally {
      setIsSending(false);
    }
  }, [isSending, conversationId, t]);

  // Send product list handler
  const handleSendProductList = useCallback(async (productIds: string[]) => {
    if (isSending || !conversationId || productIds.length === 0) return;
    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);
    try {
      const res = await apiFetch(
        `/api/operator/conversations/${encodeURIComponent(conversationId)}/actions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'send_product_list', productIds }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSendError(data.error || t('conversations.compose.sendError'));
        return;
      }
      if (data.message) {
        setMessages((prev: OperatorMessage[]) => [...prev, {
          ...data.message,
          direction: 'OUTGOING',
          text: t('conversations.compose.productSent'),
        }]);
      }
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch {
      setSendError(t('conversations.compose.sendError'));
    } finally {
      setIsSending(false);
    }
  }, [isSending, conversationId, t]);

  // Send flow handler
  const handleSendFlow = useCallback(async (flowId: string) => {
    if (isSending || !conversationId) return;
    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);
    try {
      const res = await apiFetch(
        `/api/operator/conversations/${encodeURIComponent(conversationId)}/actions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'send_flow', flowId }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSendError(data.error || t('conversations.compose.sendError'));
        return;
      }
      if (data.message) {
        setMessages((prev: OperatorMessage[]) => [...prev, {
          ...data.message,
          direction: 'OUTGOING',
          text: t('conversations.compose.flowSent'),
        }]);
      }
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch {
      setSendError(t('conversations.compose.sendError'));
    } finally {
      setIsSending(false);
    }
  }, [isSending, conversationId, t]);

  // Accept copilot suggestion
  const handleCopilotAccept = useCallback((text: string) => {
    setComposeText(text);
  }, []);

  // Fetch available operators for transfer
  const fetchOperators = useCallback(async () => {
    if (!conversation?.businessId) return;
    try {
      const res = await apiFetch(`/api/operator/conversations/team/operators?businessId=${conversation.businessId}`);
      const data = await res.json();
      if (data.success && data.operators) {
        setAvailableOperators(data.operators);
      }
    } catch (err) {
      console.error('Failed to fetch operators:', err);
    }
  }, [conversation?.businessId]);

  // Claim conversation
  const handleClaim = useCallback(async () => {
    if (!conversationId || isAssignmentLoading) return;
    setIsAssignmentLoading(true);
    try {
      const res = await apiFetch(`/api/operator/conversations/${conversationId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'claim' }),
      });
      const data = await res.json();
      if (data.success && data.conversation) {
        setConversation((prev) => prev ? { ...prev, ...data.conversation } : null);
      }
    } catch (err) {
      console.error('Failed to claim conversation:', err);
    } finally {
      setIsAssignmentLoading(false);
    }
  }, [conversationId, isAssignmentLoading]);

  // Transfer conversation
  const handleTransfer = useCallback(async (targetOperatorId: string) => {
    if (!conversationId || isAssignmentLoading) return;
    setIsAssignmentLoading(true);
    try {
      const res = await apiFetch(`/api/operator/conversations/${conversationId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'transfer', targetOperatorId }),
      });
      const data = await res.json();
      if (data.success && data.conversation) {
        setConversation((prev) => prev ? { ...prev, ...data.conversation } : null);
        setShowTransferDropdown(false);
      }
    } catch (err) {
      console.error('Failed to transfer conversation:', err);
    } finally {
      setIsAssignmentLoading(false);
    }
  }, [conversationId, isAssignmentLoading]);

  // Release assignment
  const handleUnassign = useCallback(async () => {
    if (!conversationId || isAssignmentLoading) return;
    setIsAssignmentLoading(true);
    try {
      const res = await apiFetch(`/api/operator/conversations/${conversationId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'unassign' }),
      });
      const data = await res.json();
      if (data.success && data.conversation) {
        setConversation((prev) => prev ? { ...prev, ...data.conversation } : null);
      }
    } catch (err) {
      console.error('Failed to unassign conversation:', err);
    } finally {
      setIsAssignmentLoading(false);
    }
  }, [conversationId, isAssignmentLoading]);

  // Fetch operators when transfer dropdown is opened
  useEffect(() => {
    if (showTransferDropdown && availableOperators.length === 0) {
      fetchOperators();
    }
  }, [showTransferDropdown, availableOperators.length, fetchOperators]);

  // =========================================================================
  // Render: Loading / Error / Permission states
  // =========================================================================

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!operator) return null;

  if (!permissions?.canViewConversations) {
    return (
      <div className="p-6 space-y-4">
        <Card className="text-center">
          <CardContent className="py-12">
            <Lock className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">{t('common.accessDenied')}</h2>
            <p className="text-muted-foreground">
              {t('conversations.detail.accessDenied')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/operator/conversations' })}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.back')}
        </Button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-700">{error || t('conversations.detail.notFound')}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate({ to: '/operator/conversations' })}
            >
              {t('conversations.detail.backToConversations')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =========================================================================
  // Render: Chat layout
  // =========================================================================

  return (
    <div className="h-[calc(100vh-2.75rem)] flex flex-col">
      <ChatHeader
        customerName={conversation.customerName}
        customerPhone={conversation.customerPhone}
        currentStep={conversation.currentStep}
        assignedOperatorName={conversation.assignedOperator?.name}
        onBack={() => navigate({ to: '/operator/conversations' })}
        onInfoToggle={() => setInfoPanelOpen(true)}
      />

      <MessageList
        messages={messages}
        canViewFullPII={permissions.canViewFullPII}
      />

      <CopilotSuggestionPanel
        conversationId={conversationId}
        onAccept={handleCopilotAccept}
      />

      <ComposeBar
        value={composeText}
        onChange={setComposeText}
        onSend={handleSend}
        isSending={isSending}
        sendError={sendError}
        sendSuccess={sendSuccess}
        isOutsideWindow={isOutsideWindow}
        showTemplateSelector={showTemplateSelector}
        onShowTemplateSelector={setShowTemplateSelector}
        templates={templates}
        selectedTemplate={selectedTemplate}
        onSelectTemplate={setSelectedTemplate}
        onSendTemplate={handleSendTemplate}
        products={products}
        onSendProduct={handleSendProduct}
        onSendProductList={handleSendProductList}
        flows={flows}
        onSendFlow={handleSendFlow}
      />

      <ContactPanel
        open={infoPanelOpen}
        onOpenChange={setInfoPanelOpen}
        conversation={conversation}
        operatorId={operatorId}
        isAssignmentLoading={isAssignmentLoading}
        onClaim={handleClaim}
        onUnassign={handleUnassign}
        onTransfer={handleTransfer}
        showTransferDropdown={showTransferDropdown}
        onToggleTransferDropdown={setShowTransferDropdown}
        availableOperators={availableOperators}
        flowContext={flowContext}
        journeyEvents={journeyEvents}
        journeyLoading={journeyLoading}
        journeyError={journeyError ? String(journeyError) : null}
        refetchJourney={refetchJourney}
      />
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/conversations/$id')({
  component: ConversationDetailPage,
});
